import type { ReactNode } from 'react';
import {
  EMPLOYMENT_TYPE_LABELS,
  IDEAL_CANDIDATE_PARTS,
  IDEAL_CANDIDATE_PROMPTS,
  SHIFT_DISPLAY_ZONES,
  WORK_MODE_LABELS,
  briefBulletsOf,
  hasIdealCandidate,
  idealProfileProgress,
  idealProfileSections,
  plan30Sections,
  plan90Sections,
  requestBriefTitle,
  type RequestEditView,
  type RequestPart,
  type RequisitionDetail,
} from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { TemplateSectionsView } from '../../hiring-templates/components/TemplateSectionsView';
import { EMPTY, formatIsoDate, orEmpty } from '../../../lib/format';
import { ChangeList } from './ChangeList';
import { EditedMark } from './EditedMark';
import styles from './RequestContent.module.css';

/**
 * REQ-03 (ADR 0075): what the requester wrote for the job — the role and the brief, the ideal candidate, the job's details —
 * each part with its "Edited by HR" mark, and the short list of every change someone other than the requester made.
 */

export function RequestContentCards({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  return (
    <>
      <RoleCard detail={detail} />
      <IdealCandidateCard detail={detail} />
      <JobDetailsCard detail={detail} />
      <ChangesCard edits={detail.edits} />
    </>
  );
}

function Part({ detail, part, label, children }: { readonly detail: RequisitionDetail; readonly part: RequestPart; readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className={styles.part}>
      <div className={styles.partHead}>
        <h3 className={styles.partTitle}>{label}</h3>
        <EditedMark mark={detail.editMarks[part]} />
      </div>
      {children}
    </div>
  );
}

function Points({ points }: { readonly points: readonly string[] }): ReactNode {
  if (points.length === 0) return <p className={styles.empty}>{EMPTY}</p>;
  return (
    <ul className={styles.points}>
      {points.map((point, index) => (
        <li key={`${index}-${point}`}>{point}</li>
      ))}
    </ul>
  );
}

function Prose({ text }: { readonly text: string | null }): ReactNode {
  return text && text.trim() !== '' ? <p className={styles.prose}>{text}</p> : <p className={styles.empty}>{EMPTY}</p>;
}

function RoleCard({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  return (
    <Card title="The role">
      <div className={styles.parts}>
        <Part detail={detail} part="responsibilities" label="Responsibilities">
          <Prose text={detail.responsibilities} />
        </Part>
        {(['why', 'achieve'] as const).map((key) => (
          <Part key={key} detail={detail} part={key} label={requestBriefTitle(key)}>
            <Points points={briefBulletsOf(detail.brief, key)} />
          </Part>
        ))}
        <Part detail={detail} part="managesPeople" label="This role manages people">
          <p className={styles.prose}>{detail.managesPeople ? 'Yes: the Leadership add-on applies.' : 'No'}</p>
        </Part>
        {/* ADR 0083: each plan is a template; points written before the templates are kept and shown above it. */}
        <Part detail={detail} part="plan_30" label="Onboarding plan, first 30 days after joining">
          {briefBulletsOf(detail.brief, 'plan_30').length > 0 && <Points points={briefBulletsOf(detail.brief, 'plan_30')} />}
          <TemplateSectionsView sections={plan30Sections(detail.plan30, detail.managesPeople)} />
        </Part>
        <Part detail={detail} part="plan_90" label="Onboarding plan, first 90 days after joining">
          {briefBulletsOf(detail.brief, 'plan_90').length > 0 && <Points points={briefBulletsOf(detail.brief, 'plan_90')} />}
          <TemplateSectionsView sections={plan90Sections(detail.plan90, detail.managesPeople)} />
        </Part>
      </div>
    </Card>
  );
}

/** ADR 0083: the Ideal candidate profile template, box by box; a request written before it shows its four-part text too. */
function IdealCandidateCard({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  const sections = idealProfileSections(detail.idealProfile, detail.managesPeople);
  const progress = idealProfileProgress(detail.idealProfile, detail.managesPeople);
  const isEarlier = hasIdealCandidate(detail.idealCandidate);
  const meta = sections.length === 0 ? (isEarlier ? 'Written before the template' : 'Not written') : progress.isComplete ? 'Complete' : `${progress.done} of ${progress.total} required boxes filled`;
  return (
    <Card title="The ideal candidate" meta={meta} action={<EditedMark mark={detail.editMarks.idealCandidate} />}>
      <div className={styles.parts}>
        {isEarlier &&
          IDEAL_CANDIDATE_PARTS.filter((part) => detail.idealCandidate[part].trim() !== '').map((part) => (
            <div key={part} className={styles.part}>
              <h3 className={styles.partTitle}>{IDEAL_CANDIDATE_PROMPTS[part].label}</h3>
              <Prose text={detail.idealCandidate[part]} />
            </div>
          ))}
        {(sections.length > 0 || !isEarlier) && <TemplateSectionsView sections={sections} />}
      </div>
    </Card>
  );
}

function shiftOf(detail: RequisitionDetail): string {
  const { shiftStart, shiftEnd, shiftDisplayZone, shiftHours } = detail.jobDetails;
  const zone = SHIFT_DISPLAY_ZONES.find((option) => option.value === shiftDisplayZone)?.label ?? shiftDisplayZone;
  const hours = shiftStart === null ? null : `${shiftStart} to ${shiftEnd ?? EMPTY} BD time${zone ? `, shown for ${zone}` : ''}`;
  return [hours, shiftHours].filter((part): part is string => part !== null && part !== '').join(' · ') || EMPTY;
}

function JobDetailsCard({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  const details = detail.jobDetails;
  const rows: readonly { readonly part: RequestPart; readonly label: string; readonly value: string }[] = [
    { part: 'lineManager', label: 'Line manager', value: orEmpty(detail.lineManagerName) },
    { part: 'workMode', label: 'Work mode', value: details.workMode ? WORK_MODE_LABELS[details.workMode] : 'As the position' },
    { part: 'employmentType', label: 'Employment type', value: details.employmentType ? EMPLOYMENT_TYPE_LABELS[details.employmentType] : 'As the position' },
    { part: 'location', label: 'Location', value: orEmpty(details.location) },
    { part: 'publicTitle', label: 'Title candidates see', value: orEmpty(details.publicTitle) },
    { part: 'roleFamily', label: 'Role family', value: orEmpty(details.roleFamily) },
    { part: 'targetCloseDate', label: 'Target close date', value: details.targetCloseDate ? formatIsoDate(details.targetCloseDate) : EMPTY },
    { part: 'shift', label: 'Shift', value: shiftOf(detail) },
    { part: 'isConfidential', label: 'Confidential', value: details.isConfidential ? 'Yes' : 'No' },
  ];
  return (
    <Card title="About the job" meta="Goes into the job when it is approved">
      <dl className={styles.facts}>
        {rows.map((row) => (
          <div key={row.part} className={styles.fact}>
            <dt className={styles.factLabel}>{row.label}</dt>
            <dd className={styles.factValue}>
              {row.value}
              <EditedMark mark={detail.editMarks[row.part]} />
            </dd>
          </div>
        ))}
      </dl>
      <div className={styles.parts}>
        <Part detail={detail} part="description" label="About the job, for the advert">
          <Prose text={details.description} />
        </Part>
        <Part detail={detail} part="requirements" label="Requirements, for the advert">
          <Prose text={details.requirements} />
        </Part>
      </div>
    </Card>
  );
}

/** B2: "HR and the editors will have a log" — every change, newest first, only the lines that changed (ADR 0088). */
function ChangesCard({ edits }: { readonly edits: readonly RequestEditView[] }): ReactNode {
  if (edits.length === 0) return null;
  return (
    <Card title="Changes by HR" meta={`${edits.length} ${edits.length === 1 ? 'change' : 'changes'} to what the requester wrote`}>
      <ChangeList changes={edits} />
    </Card>
  );
}
